import {
  Outlet,
  createFileRoute,
  getRouteApi,
  useRouterState,
} from "@tanstack/react-router";
import { Globe, ShieldCheck, UserRound, UsersRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Link } from "@/components/link";
import { can } from "@/lib/session";
import { cn } from "@/lib/utils";

interface SettingsTab {
  href: string;
  icon: LucideIcon;
  permission?: string;
  title: string;
}

const tabs: SettingsTab[] = [
  { href: "/admin/settings", icon: UserRound, title: "Account" },
  {
    href: "/admin/settings/team",
    icon: UsersRound,
    permission: "staff:read",
    title: "Team",
  },
  {
    href: "/admin/settings/groups",
    icon: ShieldCheck,
    permission: "groups:read",
    title: "Groups",
  },
  {
    href: "/admin/settings/site",
    icon: Globe,
    permission: "settings:read",
    title: "Site",
  },
];

const adminRoute = getRouteApi("/admin");

const SettingsLayout = () => {
  const { session } = adminRoute.useRouteContext();
  const pathname = useRouterState({
    select: (state) => state.location.pathname.replace(/\/$/u, ""),
  });

  const visibleTabs = tabs.filter(
    (tab) => !tab.permission || can(session, tab.permission)
  );

  return (
    <div className="mx-auto w-full max-w-5xl">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </header>

      <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:gap-12">
        <nav
          aria-label="Settings"
          className="-mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:w-48 lg:shrink-0 lg:flex-col lg:px-0"
        >
          {visibleTabs.map((tab) => {
            const active = pathname === tab.href;
            return (
              <Link
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                )}
                href={tab.href}
                key={tab.href}
              >
                <tab.icon aria-hidden className="size-4" />
                {tab.title}
              </Link>
            );
          })}
        </nav>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/admin/settings")({
  component: SettingsLayout,
  head: () => ({
    meta: [
      { title: "Settings" },
      {
        content: "Manage your account, team, groups and site.",
        name: "description",
      },
    ],
  }),
});
